import React, { useEffect, useState } from 'react'
import { useAuthContext } from '../../../context/auth.context'
import { lmsAdmin } from '../api'
import '../lms-admin.scss'
import { LmsIcon } from '../lms-icon'
import { LmsTrail, lmsTrail } from '../lms-trail'
import { SECTION } from '../../../shared/navigation/sections'

// Plan & Usage (docs/08 §18, design edxr-billing-plan-v1.0.html).
// Read-only display of /admin/lms/usage. Decision #7: seats = linked
// learners vs the org's limit_user. A full organization refuses new learners
// (SEAT_LIMIT_REACHED); learners already linked keep launching.

export const ViewPlanUsage = () => {
  const authContext = useAuthContext()

  const [usage, updateUsage] = useState(null)
  const [error, updateError] = useState(null)

  useEffect(() => {
    lmsAdmin.getUsage().then(updateUsage).catch(updateError)
  }, [])

  const customer = authContext.state.organizationName
  const seats = usage && usage.seats
  const limit = seats && seats.org_user_limit
  const seatPct = limit ? Math.min(100, Math.round((seats.linked_learners / limit) * 100)) : null

  return (
    <div className="lms-admin" data-testid="lms-plan-usage">
      <LmsTrail trail={lmsTrail(SECTION.lmsUsage)} />
      <div className="page-header">
        <div className="page-header-text">
          <h1 className="page-header-title">
            Plan &amp; Usage
            <span className="page-header-customer">
              <LmsIcon name="building" />
              {customer}
            </span>
          </h1>
          <p className="page-header-sub">
            Read-only usage snapshot. When the organization has no free seats, new learners can't link until a seat is
            added; learners already linked keep launching.
          </p>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {`Couldn't load usage: ${error.message}`}
        </div>
      )}

      {usage && (
        <React.Fragment>
          {seats.overage > 0 && (
            <div className="build-banner warning" data-testid="overage-banner" style={{ background: 'var(--edxr-status-warning-bg)', color: 'var(--edxr-status-warning-text)', borderColor: 'var(--edxr-status-warning-border)' }}>
              <LmsIcon name="triangle-exclamation" />
              <div>
                <strong>{seats.overage} users over the plan limit.</strong> Learners already linked keep launching, but new
                learners can't link until the organization is back under its limit. Contact EducationXR to adjust the
                plan.
              </div>
            </div>
          )}

          <div className="lms-card" style={{ marginBottom: 16, padding: '16px 18px' }}>
            <div className="section-head">
              <h3>Seats</h3>
              <span className="helper">linked LMS learners count toward seats</span>
            </div>
            <div className="summary-row" style={{ marginBottom: 0 }}>
              <div className="summary-stat">
                <div className="stat-label">Linked learners</div>
                <div className="stat-value" data-testid="stat-linked">{seats.linked_learners}</div>
                <div className="stat-sub">active account links</div>
              </div>
              <div className="summary-stat">
                <div className="stat-label">Plan limit</div>
                <div className="stat-value">{limit === null || limit === undefined ? '∞' : limit}</div>
                <div className="stat-sub">{limit === null || limit === undefined ? 'no user limit on this plan' : 'org user limit'}</div>
              </div>
              <div className="summary-stat">
                <div className="stat-label">Overage</div>
                <div className="stat-value" data-testid="stat-overage">{seats.overage}</div>
                <div className="stat-sub">reported, never gated</div>
              </div>
              <div className="summary-stat">
                <div className="stat-label">Utilization</div>
                <div className="stat-value">{seatPct === null ? '—' : `${seatPct}%`}</div>
                {seatPct !== null && (
                  <div className="progress-strip" style={{ height: 4, background: 'rgba(30,75,119,0.08)', borderRadius: 2, overflow: 'hidden', marginTop: 6 }}>
                    <div style={{ height: '100%', width: `${seatPct}%`, background: 'var(--bs-primary)', borderRadius: 2 }} />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="lms-card" style={{ marginBottom: 16, padding: '16px 18px' }}>
            <div className="section-head">
              <h3>Sessions</h3>
              <span className="helper">current calendar month, UTC</span>
            </div>
            <div className="summary-row" style={{ gridTemplateColumns: 'repeat(2, 1fr)', marginBottom: 0 }}>
              <div className="summary-stat">
                <div className="stat-label">Launched</div>
                <div className="stat-value" data-testid="stat-sessions">{usage.sessions.this_month}</div>
                <div className="stat-sub">launches this month, refused ones not counted</div>
              </div>
              <div className="summary-stat">
                <div className="stat-label">Completed</div>
                <div className="stat-value">{usage.sessions.completed_this_month}</div>
                <div className="stat-sub">with results written back</div>
              </div>
            </div>
          </div>

          <div className="lms-card" style={{ padding: '16px 18px' }}>
            <div className="section-head">
              <h3>Packages</h3>
              <span className="helper">all time</span>
            </div>
            <div className="summary-row" style={{ gridTemplateColumns: 'repeat(2, 1fr)', marginBottom: 0 }}>
              <div className="summary-stat">
                <div className="stat-label">Packages built</div>
                <div className="stat-value">{usage.packages.built_total}</div>
                <div className="stat-sub">SCORM launcher builds</div>
              </div>
              <div className="summary-stat">
                <div className="stat-label">Active connections</div>
                <div className="stat-value">{usage.packages.connections_active}</div>
                <div className="stat-sub">producing launches</div>
              </div>
            </div>
          </div>
        </React.Fragment>
      )}
    </div>
  )
}
